import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import FileUploader from "./FileUploader";

/**
 * Reusable per-entity SEO meta editor. Reads/writes onto `form` via setForm.
 * Fields: meta_title, meta_description, meta_keywords, og_image, canonical_url, robots, locale, slug
 */
export default function SeoFields({ form, setForm, showSlug = true, slugPlaceholder = "auto-generated from title" }) {
  const u = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });

  const titleLen = (form.meta_title || "").length;
  const descLen = (form.meta_description || "").length;
  const titleHint = titleLen === 0 ? "Recommended 50-60 chars" :
                    titleLen < 30 ? `Too short (${titleLen}/60)` :
                    titleLen > 60 ? `Too long (${titleLen}/60)` :
                    `Looks good (${titleLen}/60)`;
  const descHint = descLen === 0 ? "Recommended 140-160 chars" :
                   descLen < 70 ? `Too short (${descLen}/160)` :
                   descLen > 160 ? `Too long (${descLen}/160)` :
                   `Looks good (${descLen}/160)`;
  const tone = (n, min, max) => n === 0 ? "text-[#4B5563]" : n < min || n > max ? "text-[#EF4444]" : "text-[#C2410C]";

  return (
    <div className="space-y-4 pt-2 border-t border-[#E2E8F0]">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#4B5563] font-semibold">SEO · Search & Sharing</div>
      {showSlug && (
        <div className="space-y-2">
          <Label>URL slug (optional)</Label>
          <Input value={form.slug || ""} onChange={u("slug")} placeholder={slugPlaceholder} data-testid="seo-slug" />
          <p className="text-xs text-[#4B5563]">Auto-generated from title if blank. Lowercase letters, numbers, dashes only.</p>
        </div>
      )}
      <div className="space-y-2">
        <Label>Meta title</Label>
        <Input value={form.meta_title || ""} onChange={u("meta_title")} data-testid="seo-meta-title" />
        <p className={`text-xs ${tone(titleLen, 30, 60)}`}>{titleHint}</p>
      </div>
      <div className="space-y-2">
        <Label>Meta description</Label>
        <Textarea rows={2} value={form.meta_description || ""} onChange={u("meta_description")} data-testid="seo-meta-desc" />
        <p className={`text-xs ${tone(descLen, 70, 160)}`}>{descHint}</p>
      </div>
      <div className="space-y-2">
        <Label>Meta keywords (comma separated)</Label>
        <Input value={form.meta_keywords || ""} onChange={u("meta_keywords")} data-testid="seo-meta-keywords" />
      </div>
      <FileUploader
        kind="image" accept="image/*" label="Open Graph image (social share)"
        currentUrl={form.og_image}
        onUploaded={(url) => setForm({ ...form, og_image: url })}
        testId="seo-og-image"
      />
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Canonical URL</Label>
          <Input value={form.canonical_url || ""} onChange={u("canonical_url")} placeholder="optional override" data-testid="seo-canonical" />
        </div>
        <div className="space-y-2">
          <Label>Robots</Label>
          <Select value={form.robots || "default"} onValueChange={(v) => setForm({ ...form, robots: v === "default" ? "" : v })}>
            <SelectTrigger data-testid="seo-robots"><SelectValue placeholder="default (index,follow)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default (index, follow)</SelectItem>
              <SelectItem value="index,follow">index, follow</SelectItem>
              <SelectItem value="noindex,follow">noindex, follow</SelectItem>
              <SelectItem value="index,nofollow">index, nofollow</SelectItem>
              <SelectItem value="noindex,nofollow">noindex, nofollow</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Locale</Label>
        <Input value={form.locale || ""} onChange={u("locale")} placeholder="en-IN" data-testid="seo-locale" />
      </div>
    </div>
  );
}
